import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X } from 'lucide-react';
import { useToolTheme } from '@/contexts/ThemeContext';

interface WelcomePopupProps {
  onClose: () => void;
}

export function WelcomePopup({ onClose }: WelcomePopupProps) {
  const [show, setShow] = useState(false);
  const { toolColors } = useToolTheme();

  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem('hasSeenWelcome2025Q1');
    if (!hasSeenWelcome) {
      setShow(true);
    }
  }, []);

  const handleClose = () => {
    localStorage.setItem('hasSeenWelcome2025Q1', 'true');
    setShow(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="w-full max-w-lg"
          >
            <Card>
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Sparkles className="w-6 h-6" style={{ color: toolColors.primary }} />
                    </div>
                    <h2 className="text-2xl font-bold">Nouvelles fonctionnalités !</h2>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClose}
                    className="text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                <div className="mt-6 space-y-4">
                  <p className="text-gray-600">
                    Découvrez les nouvelles fonctionnalités du Baromètre Énergétique :
                  </p>
                  
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600">🎯</span>
                      <span className="text-gray-800">Notifications intelligentes pour un suivi optimal</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600">💪</span>
                      <span className="text-gray-800">Mini-exercices pratiques personnalisés</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600">📊</span>
                      <span className="text-gray-800">Nouveau tableau de bord avec historique sur 30 jours</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600">🏆</span>
                      <span className="text-gray-800">Système de défis et récompenses</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600">🤖</span>
                      <span className="text-gray-800">Assistant IA pour des recommandations personnalisées</span>
                    </li>
                  </ul>

                  <div className="mt-6 flex justify-end">
                    <Button 
                      onClick={handleClose}
                      style={{ 
                        backgroundColor: toolColors.primary,
                        color: 'white'
                      }}
                      className="hover:opacity-90"
                    >
                      Découvrir les nouveautés
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}